import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import useOnboarding from "../../../hooks/useOnboarding";
import {
  EMBED_VARIANTS,
  getOperationPreferences,
  getOpsColor,
  triggerGAEvent,
} from "../../../utils/helper";
import CycleManager from "../../Dashboard/CycleManager/CycleManager/CycleManager";
import EditorContextProvider from "../../Dashboard/Editor/EditorContextProvider";
import {
  LogIEIconSmallColored,
  LogisticsClusterIcon,
  LogisticsClusterSmallIcon,
  WFPIcon,
} from "../../Icons/Header-Icons";
import bngrc from "../../Icons/img/bngrc.jpg";
import cdema_logo from "../../Icons/img/cdema_logo.png";
import Modules from "../../Modules";
import ModuleIcon from "../../Modules/ModuleIcon";
import { Tooltip } from "../../UI";
import {
  HeaderIcon,
  ModuleIconWrapper,
  StyledDesktopHeader,
  StyledTitleWrapper,
} from "../Header-styled";
import LayerLoaderWrapper from "../components/LayerLoaderWrapper/LayerLoaderWrapper";

const getHeaderIcon = (config, isSmall = false) => {
  switch (config.header.icon) {
    case "wfp":
      return <WFPIcon />;
    case "cdema":
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <WFPIcon />
          <img
            style={{ objectFit: "contain" }}
            src={cdema_logo}
            alt={"cdema_logo"}
            width={72}
            height={72}
          />
        </div>
      );
    case "bngrc":
      return (
        <img
          style={{ objectFit: "contain" }}
          src={bngrc}
          alt={"Bngrc"}
          height={isSmall ? 50 : 30}
          width={isSmall ? 50 : 30}
        />
      );
    default:
      return (
        <a
          href="https://logcluster.org/en"
          target="_blank"
          style={{ display: "flex" }}
        >
          <LogisticsClusterIcon key="lci" />
          <LogisticsClusterSmallIcon key="lcsi" />
        </a>
      );
  }
};

const DesktopHeader = ({ config, t }) => {
  const printWidget = useSelector((state) => state.printWidget);
  const activeModule = useSelector((state) => state.activeModule);
  const cycleManagerReducer = useSelector((state) => state.cycleManagerReducer);
  const showModules = useSelector((state) => state.mapViewReducer.showModules);

  const [show, setShow] = useState(false);
  const { onBoardingTour } = useOnboarding();
  const headerTitleRef = useRef();

  useEffect(() => {
    if (!config) return;
    const { allPreferences: preferences, operationPreferences } =
      getOperationPreferences(config.id);

    if (
      !operationPreferences.activeModule &&
      Array.isArray(config.activeModules) &&
      config.activeModules.length > 1 &&
      config.embed !== EMBED_VARIANTS.WEBSITE
    ) {
      const searchParams = new URLSearchParams(window.location.search);
      const urlModule = searchParams.get("module");
      const dataCollection = searchParams.get("datacollection");
      if (urlModule && typeof urlModule === "string") {
        triggerGAEvent({
          event: "module",
          params: {
            module_name: urlModule.toUpperCase(),
          },
        });
      }

      if (urlModule || dataCollection || !showModules) return;

      setShow(true);
    }
  }, [config.id]);

  const title = t("ops." + config.id + ".title", config.title) || "";

  const handleShowModules = useCallback(() => {
    requestAnimationFrame(() => {
      setShow((prev) => !prev);
    });
  }, []);

  const opsColor = useMemo(
    () => getOpsColor(config, activeModule),
    [config, activeModule]
  );

  const AppHeader = (
    <StyledTitleWrapper show={show} ref={headerTitleRef}>
      <a style={{ position: "absolute", left: 8 }} href="/">
        <LogIEIconSmallColored width="44px" />
      </a>{" "}
      <span>{title}</span>
      {!!activeModule && !cycleManagerReducer.open && (
        <LayerLoaderWrapper opsColor={opsColor}>
          <ModuleIcon
            className="onboarding-modules loader"
            onClick={handleShowModules}
            style={{ padding: 8, gap: 4 }}
            opsColor="transparent"
            module={activeModule}
            showTitle
          >
            <Tooltip
              showOnHover
              content={t(
                "screen.widget.ModuleSwitcher." + activeModule + ".title",
                ""
              )}
              placement="bottom"
              blurred
              wrapperStyles={{
                position: "absolute",
                width: "100%",
                height: "100%",
                zIndex: 2,
                left: 0,
                top: 0,
              }}
              offset={16}
            />
          </ModuleIcon>
        </LayerLoaderWrapper>
      )}
    </StyledTitleWrapper>
  );

  const WhatHeader = (
    <StyledTitleWrapper
      style={{ background: "rgba(255, 255, 255, 1)" }}
      ref={headerTitleRef}
    >
      <a style={{ position: "absolute", left: 8 }} href="/">
        <LogIEIconSmallColored width="40px" />
      </a>{" "}
      <span>{title}</span>
      {!!activeModule && !cycleManagerReducer.open && (
        <LayerLoaderWrapper opsColor={"#dadada"}>
          <ModuleIcon
            style={{
              padding: "8px",
              gap: 8,
              background: "#dadada",
              color: "rgb(73, 73, 73)",
              paddingLeft: "44px",
              paddingRight: "18px",
            }}
            className="loader"
            onClick={handleShowModules}
            opsColor="transparent"
            module={activeModule}
            isHeader={true}
            title={t("screen.widget.ModuleSwitcher.title")}
            showTitle
            fontColor="rgb(73, 73, 73)"
          />
        </LayerLoaderWrapper>
      )}
    </StyledTitleWrapper>
  );

  return (
    <>
      <div
        style={{
          position: "fixed",
          pointerEvents: "none",
          left: "8px",
          top: "8px",
          height: "calc(100% - 44px)",
          width: "70%",
          minWidth: headerTitleRef.current
            ? headerTitleRef.current.offsetWidth + 60 + "px"
            : 0,
        }}
        id="cycle-manager"
      >
        {cycleManagerReducer.open && (
          <EditorContextProvider>
            <div
              style={{
                background: "white",
                height: "100%",
                width: "100%",
                animation: `${
                  !open ? "cycleManagerLeave" : "cycleManagerEnter"
                } 0.5s linear`,
                pointerEvents: "auto",
                borderRadius: 20,
              }}
            >
              <CycleManager />
            </div>
          </EditorContextProvider>
        )}
      </div>
      {createPortal(
        <Modules
          show={show}
          setShow={setShow}
          config={config}
          isHeaderShown={false}
          onBoardingTour={onBoardingTour}
        >
          {show && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1,
                top: 10,
                width: "max-content",
              }}
            >
              {WhatHeader}
            </div>
          )}
        </Modules>,
        document.querySelector("#portal")
      )}

      <StyledDesktopHeader id="desktop-header">
        {createPortal(
          <div
            style={{
              position: "fixed",
              zIndex: 3,
              left: "50%",
              top: "0px",
              transform: "translate(-50%, 10px)",
              display: printWidget.open && !printWidget.isLoading ? "none" : "",
            }}
          >
            {AppHeader}
          </div>,
          document.body
        )}
      </StyledDesktopHeader>
    </>
  );
};

export const ModuleIconsComponent = ({
  config,
  activeModuleIcons,
  isModuleIcons,
}) => {
  return (
    <>
      {activeModuleIcons.map((icon) => (
        <ModuleIconWrapper
          key={icon.sources.default}
          size={icon.sizes?.default}
          reduceIcons={isModuleIcons}
        >
          <a href={icon.url} target="_blank" style={{ display: "flex" }}>
            <img
              src={
                process.env.PUBLIC_URL +
                `/assets/moduleIcons/${icon.sources.default}`
              }
              alt="module-icon"
            />
          </a>
        </ModuleIconWrapper>
      ))}
      <HeaderIcon reduceIcons={isModuleIcons}>
        {getHeaderIcon(config)}
      </HeaderIcon>
    </>
  );
};

export default DesktopHeader;
